import React, { useEffect, useState } from "react";
import { Loader2, Bell } from "lucide-react";

// Normalize backend base
const rawBackend = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const BACKEND_BASE = rawBackend.replace(/\/+$|\/api$/i, "");
const API = `${BACKEND_BASE}/api`;

// Fallback posts if API fails or returns empty
const FALLBACK_POSTS = [
  {
    id: 1,
    title: "Club Video",
    embed_url: "https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Fcamsurpickleball%2Fvideos%2F1333787681850055%2F&show_text=true&width=350",
    embed_type: "facebook"
  },
  {
    id: 2,
    title: "Community Post",
    embed_url: "https://www.facebook.com/plugins/post.php?href=https%3A%2F%2Fwww.facebook.com%2Fpermalink.php%3Fstory_fbid%3Dpfbid024QpiCAyNYb31T8PDooqCbqWkYc1tXWNbot797wF3F9HT3VtipGN3GuXCEYUrw7eBl%26id%3D61582365488215&show_text=true&width=350",
    embed_type: "facebook"
  },
  {
    id: 3,
    title: "Highlights",
    embed_url: "https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2Fcamsurpickleball%2Fvideos%2F25229564406739942%2F&show_text=true&width=350",
    embed_type: "facebook"
  },
];

const FacebookPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // Fetch only 3 latest posts with cache-busting timestamp
        const timestamp = new Date().getTime();
        const res = await fetch(`${API}/embed-posts?limit=3&_t=${timestamp}`, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        if (res.ok) {
          const data = await res.json();
          console.log('Fetched embed posts:', data);
          if (data.length > 0) {
            setPosts(data);
          } else {
            setPosts(FALLBACK_POSTS.slice(0, 3));
          }
        } else {
          console.warn("Failed to fetch embed posts, using fallback");
          setPosts(FALLBACK_POSTS.slice(0, 3));
        }
      } catch (err) {
        console.warn("Error fetching embed posts, using fallback:", err);
        setError(err.message);
        setPosts(FALLBACK_POSTS.slice(0, 3));
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();

    // Poll for updates every 30 seconds to catch new posts
    const interval = setInterval(fetchPosts, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="w-full py-16 px-4" style={{
        backgroundImage: 'url(/newassets/White BG.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <div className="w-full py-8 px-4 relative" style={{
      backgroundImage: 'url(/newassets/White BG.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>
      {/* Header with Icon */}
      <div className="flex items-center justify-center mb-8">
        <div className="relative bg-[#1f6fa2] rounded-full px-8 py-3 flex items-center gap-3 shadow-lg">
          {/* decorative green base strip behind the button */}
          <span className="absolute h-12 bg-[#c7e44a] rounded-full" style={{ bottom: '-6px', left: '0', right: '0', zIndex: -1 }} />
          
          <img 
            src="/newassets/icon 1.png" 
            alt="What's New Icon" 
            className="w-8 h-8 object-contain relative z-10"
          />
          
          <h2 className="relative z-10 text-white text-2xl font-bold tracking-wide">WHAT'S NEW</h2>
        </div>
      </div>

      <div className="mx-auto max-w-7xl">
        {/* Posts Grid */}
        <div className="grid gap-8 auto-rows-fr justify-items-center grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-8">
          {posts.map((post, index) => (
            <div
              key={post.embed_id || post.id}
              className="group w-full max-w-[500px] h-full mx-auto transform transition-all duration-500 hover:-translate-y-2 flex"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="relative bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 flex-1 flex flex-col">
                {/* Gradient Accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-400"></div>
                
                {/* Card Content */}
                <div className="p-1 flex-1 flex flex-col">
                  <iframe
                    src={post.embed_url}
                    title={post.title || `Embed ${index + 1}`}
                    className="w-full flex-1 block rounded-2xl"
                    style={{ 
                      border: "none",
                      minHeight: "600px",
                      height: "100%"
                    }}
                    scrolling="no"
                    frameBorder="0"
                    allowFullScreen={true}
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  />
                </div>
                
                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-3xl"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center items-center gap-2 mb-4">
          {posts.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex 
                  ? 'w-3 h-3 bg-[#1f6fa2]' 
                  : 'w-2.5 h-2.5 bg-gray-400 hover:bg-gray-500'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FacebookPosts;